import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { InitiativeWriteInput } from '@complan/contracts';
import { usePageChrome } from '@/components/layout/page-chrome';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ApiRequestError } from '@/shared/api/api-error';
import { IntakeWizard } from './IntakeWizard';
import { useIntakeOptions, useCreateInitiative } from './intake-data';
import { fromServerFinding, type SummaryFinding } from './validation-model';

/**
 * The Initiative_Intake_Form route (`/intake`), task 23.1.
 *
 * Owns the top-bar chrome, the controlled-value option load and the create
 * submission; the wizard itself is presentational over those. On a successful
 * create it navigates to the initiative list.
 *
 * ## A rejected save is reported, not swallowed
 *
 * `POST /api/initiatives` answers a rejected payload with `validation_failed`
 * carrying the findings that blocked it (R8.2, R8.12) - an owner the
 * Identity_Service reports inactive, a Platform set with no primary. Those go back
 * into the wizard's validation summary through `submitFindings`, so the user reads
 * them in the same place as the live findings rather than watching the button do
 * nothing.
 *
 * A failure that carries *no* findings - `forbidden`, `conflict`, `internal` - has
 * nothing to put in the summary, so it surfaces as an alert above the wizard. Both
 * paths clear on the next attempt: a stale rejection displayed next to values the
 * user has since fixed is worse than no rejection at all.
 */
export function IntakePage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // A blank intake references nothing yet, so no ids are retained for R5.9 - the
  // edit route is what passes the record's current values.
  const { options, isLoading, isError } = useIntakeOptions();
  const create = useCreateInitiative();
  const [submitError, setSubmitError] = useState<unknown>(null);

  usePageChrome({ title: t('intake.title'), subtitle: t('intake.subtitle') });

  /**
   * The rejection's findings, adapted into the summary's shape.
   *
   * `fromServerFinding` is the same adapter the live re-validation uses, so a
   * finding reads identically whether it arrived from `/validate` or from a
   * refused save.
   */
  const submitFindings = useMemo<readonly SummaryFinding[]>(() => {
    if (!(submitError instanceof ApiRequestError) || submitError.body.code !== 'validation_failed') {
      return [];
    }
    return submitError.body.findings.map(fromServerFinding);
  }, [submitError]);

  /** True for a failure with nothing to show in the summary - see this module's doc. */
  const showErrorAlert =
    submitError !== null &&
    !(submitError instanceof ApiRequestError && submitError.body.code === 'validation_failed');

  const handleSubmit = useCallback(
    (payload: InitiativeWriteInput): void => {
      // Cleared before the attempt so the summary never shows a previous
      // rejection's findings beside values the user has since changed.
      setSubmitError(null);
      create.mutate(payload, {
        onSuccess: () => {
          navigate('/initiatives');
        },
        onError: (error: unknown) => {
          setSubmitError(error);
        },
      });
    },
    [create, navigate],
  );

  // Both states are reported rather than rendering the wizard against empty
  // dropdowns: every controlled field is required, so a form whose options failed
  // to load would present as "nothing is assignable" and send the user hunting for
  // master data that is in fact there.
  if (isLoading) {
    return <p className="text-muted-foreground p-4 text-[12px]">{t('common.loading')}</p>;
  }
  if (isError) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertTitle>{t('intake.optionsError.title')}</AlertTitle>
          <AlertDescription>{t('intake.optionsError.description')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {showErrorAlert ? (
        <Alert variant="destructive" className="mb-3">
          <AlertTitle>{t('intake.submitError.title')}</AlertTitle>
          <AlertDescription>{t('intake.submitError.description')}</AlertDescription>
        </Alert>
      ) : null}
      <IntakeWizard
        options={options}
        submitFindings={submitFindings}
        onSubmit={handleSubmit}
        onRequestLeave={() => navigate('/initiatives')}
      />
    </div>
  );
}
